import type { SelectProps } from '@mui/material';
import type { SyntheticEvent } from 'react';

/**
 * Utility function to create MenuProps with proper onClose handling
 * 
 * For controlled Selects (when closeHandler is provided), the handler is called
 * whenever the Menu closes—including on option selection, backdrop click,
 * Escape, and Tab—so the controlled open state stays in sync.
 * 
 * @param closeHandler - Optional function to call when dropdown should close (required for controlled Selects)
 * @param existingMenuProps - Optional existing MenuProps to merge with
 * 
 * @returns MenuProps object with proper onClose handler
 * 
 * @example
 * ```tsx
 * // Controlled Select
 * <Select
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   MenuProps={getFixedMenuProps(() => setIsOpen(false), {
 *     disablePortal: false,
 *     disableScrollLock: true
 *   })}
 * >
 *   ...
 * </Select>
 * 
 * // Uncontrolled Select (closeHandler not needed, but ensures proper onClose handling)
 * <Select
 *   MenuProps={getFixedMenuProps(undefined, {
 *     PaperProps: { style: { maxHeight: 300 } }
 *   })}
 * >
 *   ...
 * </Select>
 * ```
 */
export const getFixedMenuProps = (
  closeHandler?: () => void,
  existingMenuProps?: SelectProps['MenuProps']
): SelectProps['MenuProps'] => {
  const baseProps: SelectProps['MenuProps'] = {
    ...existingMenuProps,
    disablePortal: existingMenuProps?.disablePortal ?? false,
    disableScrollLock: existingMenuProps?.disableScrollLock ?? true,
    disableAutoFocus: existingMenuProps?.disableAutoFocus ?? true,
    disableEnforceFocus: existingMenuProps?.disableEnforceFocus ?? true,
    disableRestoreFocus: existingMenuProps?.disableRestoreFocus ?? true,
  };

  // For controlled Selects, call closeHandler whenever the Menu closes (selection, backdrop, escape, tab)
  // so the controlled open state stays in sync.
  if (closeHandler) {
    return {
      ...baseProps,
      onClose: (event: SyntheticEvent | {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (existingMenuProps?.onClose) {
          existingMenuProps.onClose(event, reason);
        }
        closeHandler();
      },
    };
  }

  return baseProps;
};

